import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";

type Theme = "dark" | "light";

interface ThemeContextType {
    theme: Theme;
    toggleTheme: (event?: React.MouseEvent) => void;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(() => {
        if (typeof window !== "undefined") {
            const stored = localStorage.getItem("theme") as Theme;
            if (stored) return stored;
            return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        }
        return "dark";
    });

    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove("dark", "light");
        root.classList.add(theme);
        localStorage.setItem("theme", theme);
    }, [theme]);

    const toggleTheme = (event?: React.MouseEvent) => {
        const isAppearanceTransition =
            // @ts-ignore
            document.startViewTransition && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (!isAppearanceTransition || !event) {
            // Fallback: instant state change + short fade
            document.body.classList.add("theme-transitioning");
            setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
            setTimeout(() => {
                document.body.classList.remove("theme-transitioning");
            }, 300);
            return;
        }

        const x = event.clientX;
        const y = event.clientY;
        const endRadius = Math.hypot(
            Math.max(x, window.innerWidth - x),
            Math.max(y, window.innerHeight - y)
        );

        const isSwitchingToDark = theme !== "dark";

        // @ts-ignore
        const transition = document.startViewTransition(() => {
            flushSync(() => {
                setThemeState(isSwitchingToDark ? "dark" : "light");
            });
            // Update root class immediately inside the transition callback
            document.documentElement.classList.remove("dark", "light");
            document.documentElement.classList.add(isSwitchingToDark ? "dark" : "light");
        });

        transition.ready.then(() => {
            const clipPath = [
                `circle(0px at ${x}px ${y}px)`,
                `circle(${endRadius}px at ${x}px ${y}px)`,
            ];

            document.documentElement.animate(
                {
                    clipPath: clipPath,
                },
                {
                    duration: 500,
                    easing: "cubic-bezier(0.25, 1, 0.5, 1)",
                    pseudoElement: "::view-transition-new(root)",
                }
            );
        });
    };

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}
